import { useEffect, useRef } from 'react';
import { useSlotCanvasStore, type SlotExperience } from '@/store/slotCanvasStore';
import { useSlotCanvasContext } from './SlotCanvasContext';

export interface UseRegisterSlotCanvasArgs {
  id: string;
  experience: SlotExperience;
  title: string;
  chatHistoryId?: string;
  /**
   * Open the canvas on mount even while the context signals
   * `suppressAutoOpen` (history hydration / preloaded replay). For
   * host-driven slots — e.g. the report preview bound to the conversation —
   * which must be visible from first render regardless of how the thread
   * was entered. Message-driven behaviour slots leave this off.
   */
  ignoreSuppressAutoOpen?: boolean;
  /**
   * Register the slot without opening it on mount. Used by slots that should be
   * reachable (navigator / an explicit "View") but must not steal the canvas —
   * e.g. artifacts mounted by the briefing panel, which stays open until the
   * user opens an artifact. Defaults to true (auto-open per the flags above).
   */
  autoOpen?: boolean;
  /** Preferred default width (px) for this slot when the user hasn't resized. */
  preferredWidthPx?: number;
  /**
   * Whether this slot renders an in-stream preview chip the user can click to
   * reopen it. Controls whether the canvas frame shows a close (✕) button (vs.
   * collapse-only). Defaults to true — behaviour slots render a chip. Host-driven
   * slots with no chip (report canvas, briefing) must pass false so they can't be
   * closed into an unreachable state.
   */
  hasInStreamCard?: boolean;
}

export interface UseRegisterSlotCanvasResult {
  /** True when this slot is the currently-open one in the canvas. */
  isOpen: boolean;
  /** Opens the canvas to this slot. No-op in inline mode. */
  open: () => void;
}

/**
 * Registers a behaviour slot with the global canvas store and (when appropriate)
 * auto-opens the canvas on mount. Safe to call in both inline and canvas mode —
 * in inline mode it is a no-op.
 *
 * The three concerns are deliberately kept in separate effects, because they
 * have different lifetimes:
 *
 *   1. Registration + metadata — re-runs freely as title / chatHistoryId arrive.
 *   2. Auto-open — a ONE-SHOT per mounted slot id. It must never re-fire when
 *      the surrounding flags change: `suppressAutoOpen` flips from true to false
 *      the moment the user sends a message in a thread that had history, and a
 *      re-fire would (a) yank the canvas open onto whichever historical slot
 *      registered last and (b) reopen a panel the user had just closed.
 *   3. Unregistration — on unmount only, so a flag change can't tear the slot
 *      out of the registry while it is still on screen.
 */
export function useRegisterSlotCanvas({
  id,
  experience,
  title,
  chatHistoryId,
  ignoreSuppressAutoOpen = false,
  autoOpen = true,
  preferredWidthPx,
  hasInStreamCard = true,
}: UseRegisterSlotCanvasArgs): UseRegisterSlotCanvasResult {
  const context = useSlotCanvasContext();
  const registerSlot = useSlotCanvasStore((s) => s.registerSlot);
  const unregisterSlot = useSlotCanvasStore((s) => s.unregisterSlot);
  const openSlot = useSlotCanvasStore((s) => s.openSlot);
  const isOpen = useSlotCanvasStore((s) => s.openSlotId === id);

  const active = context.mode === 'canvas';
  const suppressAutoOpen = context.suppressAutoOpen;

  // Whether this slot would auto-open *right now*. Mirrored into a ref so the
  // one-shot effect below reads the value as of the moment the slot mounted,
  // without taking a dependency that would re-fire it later.
  const shouldAutoOpenRef = useRef(false);
  shouldAutoOpenRef.current =
    autoOpen && (ignoreSuppressAutoOpen || !suppressAutoOpen);

  // Registration + metadata. Runs before the auto-open effect below (effects
  // fire in declaration order), so the registry entry always exists by the time
  // the canvas is asked to open onto it.
  useEffect(() => {
    if (!active) return;
    registerSlot({
      id,
      experience,
      title,
      chatHistoryId,
      preferredWidthPx,
      hasInStreamCard,
      registeredAt: Date.now(),
    });
  }, [active, id, experience, title, chatHistoryId, preferredWidthPx, hasInStreamCard, registerSlot]);

  // Auto-open: once per mounted slot id, never re-evaluated afterwards.
  useEffect(() => {
    if (!active) return;
    if (!shouldAutoOpenRef.current) return;
    openSlot(id);
  }, [active, id, openSlot]);

  // Unregistration: unmount only. Tied to the slot id alone so no flag change
  // can pull a visible slot out of the registry mid-conversation.
  useEffect(() => {
    if (!active) return;
    return () => {
      unregisterSlot(id);
    };
  }, [active, id, unregisterSlot]);

  return {
    isOpen: active && isOpen,
    open: () => {
      if (active) openSlot(id);
    },
  };
}
